import { Check, ChevronsUpDown, UserPlus, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CustomerModel } from '@/http/models/customer/Customer'
import { CustomerModel as CustomerType } from '@/types/model/Customer'
import { Spinner } from '@/components/ui/spinner'

interface CustomerSearchProps {
  customerInfo: { name: string; phone: string }
  setCustomerInfo: (info: { name: string; phone: string }) => void
  selectedCustomer: CustomerType.Customer | null
  setSelectedCustomer: (c: CustomerType.Customer | null) => void
}

export const CustomerSearch = ({
  customerInfo,
  setCustomerInfo,
  selectedCustomer,
  setSelectedCustomer,
}: CustomerSearchProps) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<CustomerType.Customer[]>([])
  const [loading, setLoading] = useState(false)
  const [manualMode, setManualMode] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!open) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      if (!query.trim()) {
        setResults([])
        return
      }
      setLoading(true)
      try {
        const res = await CustomerModel.getList({
          query: { search: query.trim(), page: 1, length: 10 },
        })
        if (res.success) {
          setResults((res.data as any)?.result ?? [])
        } else {
          setResults([])
        }
      } catch {
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 300)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, open])

  const handleSelect = (customer: CustomerType.Customer) => {
    setSelectedCustomer(customer)
    setCustomerInfo({ name: customer.name, phone: customer.phone ?? '' })
    setManualMode(false)
    setOpen(false)
  }

  const handleClear = () => {
    setSelectedCustomer(null)
    setCustomerInfo({ name: '', phone: '' })
    setManualMode(false)
    setQuery('')
  }

  const handleManual = () => {
    setSelectedCustomer(null)
    setManualMode(true)
    setOpen(false)
  }

  if (selectedCustomer) {
    return (
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 text-sm">
          <p className="truncate font-medium text-ink">{selectedCustomer.name}</p>
          <p className="tabular-nums text-text-2">{selectedCustomer.phone}</p>
        </div>
        <Button variant="ghost" size="icon" aria-label="Remove customer" onClick={handleClear}>
          <X />
        </Button>
      </div>
    )
  }

  if (manualMode) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="flex items-center gap-1.5 text-sm font-medium text-ink">
            <UserPlus className="size-4 text-text-2" /> New customer
          </p>
          <Button variant="link" onClick={handleClear}>
            Search instead
          </Button>
        </div>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="new-customer-name" className="text-sm text-ink">Name</Label>
            <Input
              id="new-customer-name"
              value={customerInfo.name}
              onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
              placeholder="Customer name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-customer-phone" className="text-sm text-ink">Phone</Label>
            <Input
              id="new-customer-phone"
              value={customerInfo.phone}
              onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
              placeholder="Customer phone"
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-8 w-full justify-between rounded-md px-3 text-sm font-normal text-muted-foreground">
          Search or create a customer
          <ChevronsUpDown className="text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-64 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name or phone"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {loading && (
              <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
                <Spinner className="size-4" />
                Searching…
              </div>
            )}
            {!loading && query.trim() && results.length === 0 && (
              <CommandEmpty>No customer found.</CommandEmpty>
            )}
            {!loading && results.length > 0 && (
              <CommandGroup heading="Customers">
                {results.map((c) => (
                  <CommandItem key={c._id} value={c._id} onSelect={() => handleSelect(c)}>
                    <Check
                      className={cn(
                        'mr-2 size-4',
                        selectedCustomer === c ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <div className="flex flex-col">
                      <span className="font-medium text-ink">{c.name}</span>
                      <span className="text-xs text-muted-foreground">{c.phone}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            <CommandSeparator />
            <CommandGroup>
              <CommandItem onSelect={handleManual}>
                <UserPlus className="mr-2 size-4" />
                Create a new customer
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
